<template>
    <div
        v-click-outside="closeDropdown"
        class="client-search ppv2-relative ppv2-z-10 ppv2-w-fit ppv2-min-w-[330px] ppv2-max-w-[470px] ppv2-flex ppv2-flex-col ppv2-gap-[6px]"
    >
        <label
            class="client-search__label poppinsMedium ppv2-m-0 ppv2-text-[10.5px] poppinsSemiBold ppv2-uppercase ppv2-tracking-[0.09em] ppv2-text-[#9aa4b2]"
            :for="inputId"
        >
            {{ $t('devisV3.clientSearch.label') }}
        </label>

        <div
            class="client-search__field ppv2-box-border ppv2-flex ppv2-h-[40px] ppv2-items-center ppv2-gap-[10px] ppv2-rounded-[11px] ppv2-border-[1.5px] ppv2-border-solid ppv2-border-[#cbd2dc] ppv2-px-[13px] hover:ppv2-border-[#7d2dbd]"
            :class="{ 'is-open': isOpen }"
        >
            <Search class="client-search__icon ppv2-shrink-0 ppv2-text-[#7d2dbd]" :size="17" />
            <input
                :id="inputId"
                ref="input"
                v-model="query"
                type="text"
                class="client-search__input poppinsRegular ppv2-min-w-0 ppv2-flex-1 ppv2-border-0 ppv2-bg-transparent ppv2-text-sm ppv2-text-[#111827] ppv2-outline-none"
                autocomplete="off"
                :placeholder="$t('devisV3.clientSearch.placeholder')"
                @input="onType"
                @focus="onFocus"
                @keydown.esc.stop="closeDropdown"
            />
            <v-progress-circular
                v-if="loading"
                class="client-search__spinner"
                indeterminate
                color="#7D2DBD"
                size="16"
                width="2"
            />
        </div>

        <!-- La liste ne s'ouvre qu'à partir du minimum de saisie, celui-là même qu'impose
             AutocompleteClientRequest : sous deux caractères le serveur refuserait l'appel. -->
        <div
            v-if="isOpen"
            class="client-search__dropdown ppv2-absolute ppv2-left-0 ppv2-top-[70px] ppv2-z-[60] ppv2-w-full ppv2-overflow-hidden ppv2-rounded-[11px] ppv2-border ppv2-border-solid ppv2-border-[#e2e6ec] ppv2-bg-white dark:ppv2-bg-dark ppv2-shadow-[0_14px_30px_rgba(2,24,50,0.14)]"
        >
            <button
                type="button"
                class="client-search__create ppv2-flex ppv2-w-full ppv2-items-center ppv2-gap-[10px] ppv2-border-0 ppv2-border-b ppv2-border-solid ppv2-border-[#e2e6ec] ppv2-bg-[#cea3e326] ppv2-px-3 ppv2-py-[10px] hover:ppv2-bg-[#954fd333]"
                @click="requestCreation"
            >
                <span
                    class="client-search__plus ppv2-flex ppv2-h-[26px] ppv2-w-[26px] ppv2-shrink-0 ppv2-items-center ppv2-justify-center ppv2-rounded-[7px] ppv2-bg-[#7d2dbd] ppv2-text-white"
                >
                    <Plus :size="14" />
                </span>
                <span class="client-search__identity ppv2-flex ppv2-min-w-0 ppv2-flex-col ppv2-gap-px ppv2-text-left">
                    <span class="client-search__create-label poppinsRegular ppv2-text-[13px] poppinsSemiBold ppv2-text-[#571f84]">
                        {{
                            trimmedQuery.length >= 3
                                ? $t('devisV3.clientSearch.createTyped', { query: trimmedQuery })
                                : $t('devisV3.clientSearch.createBlank')
                        }}
                    </span>
                    <span class="client-search__meta poppinsRegular ppv2-text-[11px] ppv2-text-[#7d2dbd]">{{ createHint }}</span>
                </span>
            </button>

            <p
                v-if="!trimmedQuery"
                class="client-search__recent-title poppinsMedium ppv2-m-0 ppv2-bg-[#dce1eb33] ppv2-px-3 ppv2-py-[7px] ppv2-text-[10px] poppinsSemiBold ppv2-uppercase ppv2-tracking-[0.08em] ppv2-text-[#9aa4b2]"
            >
                {{ $t('devisV3.clientSearch.recent') }}
            </p>
            <ul v-if="results.length" class="client-search__list">
                <li
                    v-for="client in results"
                    :key="client.id"
                    class="client-search__row ppv2-flex ppv2-cursor-pointer ppv2-items-center ppv2-gap-[10px] ppv2-border-b ppv2-border-solid ppv2-border-[#dce1eb33] ppv2-px-3 ppv2-py-[10px] hover:ppv2-bg-[#e1d7f733]"
                    @click="pick(client)"
                >
                    <span
                        class="client-search__avatar poppinsSemiBold ppv2-flex ppv2-h-[26px] ppv2-w-[26px] ppv2-shrink-0 ppv2-items-center ppv2-justify-center ppv2-rounded-[7px] ppv2-bg-[#f3eafa] ppv2-text-[11px] ppv2-text-[#7d2dbd]"
                        >{{ initials(client) }}</span
                    >
                    <span class="client-search__identity ppv2-flex ppv2-min-w-0 ppv2-flex-1 ppv2-flex-col ppv2-text-left">
                        <span
                            class="client-search__name poppinsSemiBold ppv2-overflow-hidden ppv2-text-ellipsis ppv2-whitespace-nowrap ppv2-text-[13px]"
                            >{{ nameOf(client) }}</span
                        >
                        <span
                            class="client-search__meta poppinsRegular ppv2-overflow-hidden ppv2-text-ellipsis ppv2-whitespace-nowrap ppv2-text-[11px] ppv2-text-[#9aa4b2]"
                            >{{ metaOf(client) }}</span
                        >
                    </span>
                </li>
            </ul>

            <p v-else-if="!loading" class="client-search__empty poppinsRegular">
                {{ $t('devisV3.clientSearch.noResults') }}
            </p>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import lodash from 'lodash'
import { Search, Plus } from 'lucide-vue'
import { classifyClientPrefill, CLIENT_PREFILL_MIN_CHARS } from '../../../../utils/clientPrefill'

/**
 * Champ de recherche du bloc « Client » d'un devis.
 *
 * Purement présentation : il interroge l'autocomplétion et REMONTE le choix. Il n'enregistre
 * rien, ne rattache rien au devis et ne crée aucune fiche — la création reste celle du popup
 * existant, que le parent ouvre sur l'évènement `create`.
 */
export default {
    name: 'ClientSearchSelect',
    directives: {
        // Même directive locale que DateRangePicker : le projet ne charge pas celle de Vuetify.
        'click-outside': {
            bind(el, binding, vnode) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        vnode.context[binding.expression](event)
                    }
                }
                document.body.addEventListener('click', el.clickOutsideEvent)
            },
            unbind(el) {
                document.body.removeEventListener('click', el.clickOutsideEvent)
            },
        },
    },
    components: { Search, Plus },
    props: {
        // Client déjà rattaché au devis, pour que le champ montre le nom en place plutôt
        // qu'un champ vide après un rechargement de page.
        selected: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            query: '',
            results: [],
            loading: false,
            isOpen: false,
            inputId: `client-search-${this._uid}`,
        }
    },
    computed: {
        trimmedQuery() {
            return this.query.trim()
        },
        createHint() {
            if (this.trimmedQuery.length < CLIENT_PREFILL_MIN_CHARS) {
                return this.$t('devisV3.clientSearch.blankHint')
            }

            return this.$t(`devisV3.clientSearch.prefillHints.${classifyClientPrefill(this.trimmedQuery)}`)
        },
    },
    watch: {
        selected: {
            immediate: true,
            handler(client) {
                // Une sélection venue du parent (popup, client de passage, devis rouvert)
                // se reflète dans le champ ; la vider laisse le champ vide.
                this.query = client ? this.nameOf(client) : ''
            },
        },
    },
    created() {
        // 300 ms : la frappe d'un nom complet ne déclenche alors qu'un appel ou deux.
        this.debouncedSearch = lodash.debounce(this.runSearch, 300)
    },
    beforeDestroy() {
        this.debouncedSearch?.cancel()
    },
    methods: {
        onType() {
            if (!this.trimmedQuery) {
                this.debouncedSearch.cancel()
                this.results = []
                this.loadRecent()
                return
            }

            if (this.trimmedQuery.length < 2) {
                this.debouncedSearch.cancel()
                this.results = []
                this.loading = false
                this.isOpen = true
                return
            }

            this.isOpen = true
            this.loading = true
            this.debouncedSearch()
        },
        onFocus() {
            this.isOpen = true
            if (!this.trimmedQuery && !this.results.length) this.loadRecent()
        },
        async loadRecent() {
            this.loading = true
            try {
                const { data } = await axios.get('/back-office/v3/devis/clients/search')
                if (!this.trimmedQuery) this.results = data.data || []
            } catch (error) {
                this.results = []
            } finally {
                this.loading = false
            }
        },
        async runSearch() {
            const term = this.trimmedQuery

            if (term.length < 2) {
                this.loading = false
                return
            }

            try {
                const { data } = await axios.get('/back-office/v3/devis/clients/search', {
                    params: { q: term },
                })

                // Une réponse arrivée après une frappe plus récente est ignorée : sans ce
                // contrôle, une requête lente réécrirait la liste d'une recherche abandonnée.
                if (term !== this.trimmedQuery) {
                    return
                }

                this.results = data.data || []
            } catch (error) {
                console.error('Error searching clients:', error)
                this.results = []
            } finally {
                if (term === this.trimmedQuery) {
                    this.loading = false
                }
            }
        },
        pick(client) {
            this.closeDropdown()
            this.query = this.nameOf(client)
            this.$emit('select', client)
        },
        requestCreation() {
            this.closeDropdown()
            const term = this.trimmedQuery.length >= CLIENT_PREFILL_MIN_CHARS ? this.trimmedQuery : ''
            this.$emit('create', term)
        },
        closeDropdown() {
            this.isOpen = false
        },
        clearQuery() {
            this.query = ''
            this.results = []
            this.isOpen = true
            this.loadRecent()
        },
        /**
         * Nom affiché : celui que le serveur a déjà choisi (`display_name` retient la raison
         * sociale d'un professionnel), sinon le nom civil recomposé — le parent transmet
         * aussi des fiches venues d'ailleurs, qui n'ont pas cette clé.
         */
        nameOf(client) {
            if (client.display_name) {
                return client.display_name
            }

            const full = `${client.first_name || ''} ${client.last_name || ''}`.trim()

            return full || client.social_reason || client.email || ''
        },
        initials(client) {
            const parts = this.nameOf(client).split(/\s+/).filter(Boolean).slice(0, 2)

            return parts.map((part) => part.charAt(0).toUpperCase()).join('') || '?'
        },
        /**
         * Sous-ligne « Ville · email ». L'email distingue deux homonymes, ce que le nombre de
         * devis ne faisait pas.
         *
         * Les deux morceaux manquent chacun de leur côté — une fiche sans adresse, une fiche
         * sans email — d'où l'assemblage par filtrage : le séparateur ne subsiste jamais seul
         * en tête ou en fin de ligne.
         */
        metaOf(client) {
            const contact = client.is_professional ? `${client.first_name || ''} ${client.last_name || ''}`.trim() : ''
            const quotes = `${client.quotes_count || 0} devis`

            return [contact, client.city, quotes].filter(Boolean).join(' · ')
        },
    },
}
</script>

<style lang="scss" scoped>
$purple: #7d2dbd;
$purple-soft: #f3eafa;

.client-search {
    width: fit-content;
}

.client-search__label {
    display: block;
}

.client-search__field {
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;

    &:focus-within,
    &.is-open {
        border-color: $purple;
        box-shadow: 0 0 0 3px rgba(125, 45, 189, 0.12);
    }
}

.client-search__icon {
    color: $purple;
}

.client-search__input {
    &::placeholder {
        color: #9ca3af;
    }
}

.client-search__spinner {
    flex-shrink: 0;
}

.client-search__dropdown {
    min-width: 100%;
}

.client-search__list {
    margin: 0;
    padding: 0;
    max-height: 260px;
    overflow-y: auto;
    list-style: none;
}

.client-search__row {
    transition: background-color 0.12s ease;
}

.client-search__avatar {
    line-height: 1;
}

.client-search__identity {
    min-width: 0;
}

.client-search__name {
    line-height: 1.3;
}

.client-search__meta {
    line-height: 1.3;
}

.client-search__empty {
    margin: 0;
    padding: 14px 12px;
    border-bottom: 1px solid #f3f4f6;
    color: #9ca3af;
    font-size: 13px;
}

.client-search__create {
    cursor: pointer;
    transition: background-color 0.12s ease;
}

.client-search__plus {
    line-height: 1;
}

.client-search__create-label {
    line-height: 1.3;
}
</style>
